import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOSchema from '../components/SEOSchema';
import { medicalTeam } from '../data/mock';
import { 
  Users, 
  Stethoscope, 
  Heart, 
  Baby, 
  Apple, 
  ArrowRight,
  Award,
  GraduationCap,
  Shield
} from 'lucide-react';

const TeamPage = () => {
  // Single source of truth: team data lives in data/mock.js (medicalTeam)
  const iconMap = { Stethoscope, Apple, Heart, Users, Baby };
  const teamMembers = medicalTeam.map((m) => {
    const Icon = iconMap[m.icon] || Stethoscope;
    return {
      id: m.id,
      name: m.name,
      role: m.role,
      qualification: m.qualifications,
      specialization: m.specialty,
      color: m.color,
      icon: <Icon className="w-8 h-8" />,
      expertise: m.expertise,
      description: m.description,
      philosophy: m.philosophy,
    };
  });

  const teamApproach = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Integrated Care Model",
      description: "Our team works collaboratively, not in silos. Your medical lead, specialist consultants, and nutritionists share insights to create comprehensive care plans."
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Continuous Learning",
      description: "Healthcare evolves constantly. Our team stays current with the latest research, guidelines, and evidence-based practices in nutrition and medicine."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Patient-Centered Focus",
      description: "Every recommendation is made with your individual circumstances in mind. We do not believe in one-size-fits-all approaches."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Medical Team | Doctors, Specialists & Nutritionists | TCUBE FITNESS</title>
        <meta name="description" content="Meet the TCUBE FITNESS medical team. MBBS doctors, MS gynecologists, fertility specialists, and certified nutritionists working together for your health transformation." />
        <meta name="keywords" content="TCUBE FITNESS team, doctor nutritionist, medical fitness team, gynecologist nutritionist, fertility nutrition specialist, qualified nutritionist, MBBS doctor fitness" />
        <link rel="canonical" href="https://tcubefitness.com/our-team" />
        <meta property="og:title" content="Our Medical Team | TCUBE FITNESS" />
        <meta property="og:description" content="Meet our multidisciplinary team of doctors, specialists, and certified nutritionists." />
        <meta property="og:url" content="https://tcubefitness.com/our-team" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="webpage"
        name="TCUBE FITNESS™ Medical Team"
        description="Meet the TCUBE FITNESS multidisciplinary medical team, including Dr. Pooja Sree, clinical nutritionist K. Pavani, gynecologist Dr. Mrudula, and our 11+ specialist network."
        url="/our-team"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Our Team', path: '/our-team' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
                <Users className="w-4 h-4" />
                Multidisciplinary Medical Team
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Meet the Experts Behind Your Transformation
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                At TCUBE FITNESS, your health is guided by a team of qualified medical professionals and certified nutritionists. Together, we bring clinical expertise, specialized knowledge, and personalized attention to every client's journey.
              </p>
            </div>
          </div>
        </section>

        {/* Team Members Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="space-y-12">
              {teamMembers.map((member, idx) => (
                <div 
                  key={member.id}
                  className={`bg-slate-900/50 rounded-3xl border border-slate-800 overflow-hidden ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                >
                  <div className="md:flex">
                    {/* Left Column - Info */}
                    <div className="md:w-2/5 p-8 flex flex-col justify-center items-center text-center border-b md:border-b-0 md:border-r border-slate-800">
                      <div className={`w-24 h-24 bg-${member.color}-500/10 rounded-2xl flex items-center justify-center text-${member.color}-400 mb-6`}>
                        {member.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">{member.name}</h2>
                      <p className={`text-${member.color}-400 font-semibold mb-2`}>{member.role}</p>
                      <p className="text-slate-400 text-sm mb-1">{member.qualification}</p>
                      <p className="text-slate-500 text-sm">{member.specialization}</p>
                      
                      {/* Expertise Tags */}
                      <div className="flex flex-wrap justify-center gap-2 mt-6">
                        {member.expertise.map((skill, i) => (
                          <span 
                            key={i}
                            className={`px-3 py-1 bg-${member.color}-500/10 text-${member.color}-400 text-xs rounded-full`}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Right Column - Description */}
                    <div className="md:w-3/5 p-8 flex flex-col justify-center">
                      <p className="text-slate-300 leading-relaxed mb-6">
                        {member.description}
                      </p>
                      
                      {member.philosophy && (
                        <blockquote className={`border-l-4 border-${member.color}-500 pl-4 py-2`}>
                          <p className="text-slate-400 italic">"{member.philosophy}"</p>
                        </blockquote>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Approach Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Our <span className="text-blue-400">Team Approach</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                How our multidisciplinary team works together for your health
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {teamApproach.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800"
                >
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-400 mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Credentials Matter Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-blue-900/30 via-indigo-900/30 to-blue-900/30 rounded-3xl p-8 border border-blue-500/20">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">
                  Why Qualifications Matter
                </h2>
                
                <div className="space-y-4 text-slate-300">
                  <p>
                    In an industry filled with self-proclaimed experts and influencer advice, we believe credentials matter. When your health is at stake, you deserve guidance from professionals who have undergone rigorous training and are accountable to medical standards.
                  </p>
                  <p>
                    Our team's qualifications mean we understand not just how to create meal plans, but why certain approaches work for specific conditions. We recognize drug-nutrient interactions, understand hormonal complexities, and can safely guide those with medical conditions toward better health.
                  </p>
                  <p>
                    This is particularly important for clients with conditions like diabetes, thyroid disorders, PMOS, or those who are pregnant. Generic fitness advice can be dangerous in these situations. Our medical oversight ensures your path to health is safe and appropriate for your individual circumstances.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 via-cyan-900/50 to-blue-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready to Work with Our Team?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Book a consultation to discuss your health goals with our medical experts.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Book Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Programs
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Our Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-emerald-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-emerald-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/online-fitness-coaching" className="text-slate-400 hover:text-orange-400 transition-colors">Online Fitness Coaching</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TeamPage;
